import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  DigitalQuestion,
  DigitalSubmission,
  DigitalSubmissionMultiChoice,
  QuestionTypeV2,
  PartialBy,
} from '../../redux/types';
import HeadingContainerDRT from './HeadingContainerDRT';
import MultiChoiceQuestion from '../questions/multiChoice/MultiChoiceQuestion';
import { DigitalSubmissionRequest } from '../../loopback/apiClient/DRTApi';
import ApiClient from '../../loopback/apiClient';
import { isAnswerEmpty } from '../utilities/digitalQuestionUtil';

type Props = {
  contextQuestions: DigitalQuestion[];
  digitalSubmissions?: DigitalSubmission[];
  sectionId: number;
  enrollmentId?: number;
  trialId: number;
  addOrUpdateDigitalSubmission: (submission: DigitalSubmission) => void;
  finalizeContextSubmissionAction: (trialId: number, sectionId: number) => void;
  onAfterFinalize: () => void;
}

const ERROR_MESSAGE = 'Please answer all the questions';
const DigitalContextQuestions: React.FC<Props> = ({
  contextQuestions,
  digitalSubmissions,
  sectionId,
  enrollmentId,
  trialId,
  addOrUpdateDigitalSubmission,
  finalizeContextSubmissionAction,
  onAfterFinalize,
}: Props) => {
  const anchorEl = useRef<HTMLElement>();
  const contextSubmissions = digitalSubmissions?.filter((q) => !q.digitalCaseId);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (errorMessage && anchorEl.current) {
      anchorEl.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [errorMessage]);

  const onSubmitContextQuestions = () => {
    if (!contextSubmissions || contextSubmissions.length === 0) {
      setErrorMessage(ERROR_MESSAGE);
      return;
    }

    const emptyAnswer = contextSubmissions.filter((submission) => isAnswerEmpty(submission));

    if (emptyAnswer.length > 0) {
      setErrorMessage(ERROR_MESSAGE);
      return;
    }
    finalizeContextSubmissionAction(trialId, sectionId);
    onAfterFinalize();
  };

  const onSaveHandler = useCallback(async (val?: PartialBy<DigitalSubmission, 'id'>) => {
    if (errorMessage) {
      setErrorMessage(undefined);
      anchorEl.current = undefined;
    }

    if (val === undefined) {
      return;
    }
    const reqPayload: DigitalSubmissionRequest = {
      answer: val.answer,
      digitalQuestionId: val.digitalQuestionId,
      digitalSectionId: val.digitalSectionId,
      nonEvaluable: true,
      type: val.type,
      trialId,
    };
    const savedSubmission = await ApiClient.addOrUpdateDigitalSubmission(reqPayload);
    addOrUpdateDigitalSubmission(savedSubmission);
  }, [addOrUpdateDigitalSubmission, errorMessage, trialId]);

  if (!enrollmentId) {
    return null;
  }

  return (
    <HeadingContainerDRT heading="Context Questions">
      {errorMessage && (
        <Box component="span" ref={anchorEl}>
          <Typography sx={{ color: 'red' }}>{errorMessage}</Typography>
        </Box>
      )}
      <Box>
        {
          contextQuestions.map((question) => (
            <Box key={question.id} sx={{ p: 2 }}>
              {
                question.type === 'MultiChoice' && (
                  <MultiChoiceQuestion
                    mode="submit"
                    question={question}
                    enrollmentId={enrollmentId}
                    sectionId={sectionId}
                    defaultSubmission={
                      contextSubmissions?.find((s) => (
                        s.digitalQuestionId === question.id
                        && s.digitalSectionId === sectionId
                        && s.type === QuestionTypeV2.MultiChoice
                      )) as DigitalSubmissionMultiChoice
                    }
                    onSave={onSaveHandler}
                  />
                )
              }
            </Box>
          ))
        }
      </Box>
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" color="tertiary" onClick={onSubmitContextQuestions}>
          Submit
        </Button>
      </Box>
    </HeadingContainerDRT>
  );
};

export default DigitalContextQuestions;
